import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAIAnalysisLimit } from '../hooks/useSubscription';
import PricingModal from './PricingModal';
import { Capacitor } from '@capacitor/core';

import AICoachIcon from '../assets/AI Coach Default.png';
import WorkoutHistoryIcon from '../assets/Workout History Default.png';
import DietTrackerIcon from '../assets/Diet Tracker Default.png';
import DumbbellIcon from '../assets/Dumbbell Default.png';
import BlogDefaultIcon from '../assets/Blog Default.png';
import BullseyeIcon from '../assets/Bullseye Default.png';
import PadlockIcon from '../assets/Padlock Default.png';
import CalendarIcon from '../assets/Calendar Default.png';

export default function AICoach({ user }) {
  const aiLimit = useAIAnalysisLimit(user);
  const [showPricing, setShowPricing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [coachReport, setCoachReport] = useState('');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryReport, setSelectedHistoryReport] = useState(null);
  const [userData, setUserData] = useState({
    profile: {},
    measurements: [],
    workouts: [],
    meals: [],
    personalBests: {}
  });
  const [lastReportDate, setLastReportDate] = useState(null);

  // Fetch all user data for analysis
  const fetchAllUserData = useCallback(async () => {
    if (!user?.id) return;

    setLoadingStage('Loading your fitness data...');
    setProgress(20);

    try {
      // Fetch profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setProgress(40);

      // Fetch recent measurements (last 6 months)
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const { data: measurements } = await supabase
        .from('body_measurements')
        .select('*')
        .eq('user_id', user.id)
        .gte('measurement_date', sixMonthsAgo.toISOString().split('T')[0])
        .order('measurement_date', { ascending: true });

      setProgress(60);

      // Fetch recent workouts (last 3 months)
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const { data: workouts } = await supabase
        .from('workouts')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', threeMonthsAgo.toISOString().split('T')[0])
        .order('date', { ascending: true });

      setProgress(80);

      // Fetch recent meals (last month)
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      const { data: meals } = await supabase
        .from('meals')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', oneMonthAgo.toISOString().split('T')[0])
        .order('date', { ascending: true });

      // Calculate personal bests
      const personalBests = {};
      workouts?.forEach((w) => {
        if (!w.exercise || !w.weight) return;
        const name = w.exercise;
        if (!personalBests[name] || w.weight > personalBests[name].weight) {
          personalBests[name] = {
            weight: w.weight,
            date: w.date,
            reps: w.reps
          };
        }
      });

      setUserData({
        profile: profile || {},
        measurements: measurements || [],
        workouts: workouts || [],
        meals: meals || [],
        personalBests
      });

      setProgress(100);
      setLoadingStage('Data loaded successfully!');
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load your fitness data');
      setProgress(0);
      setLoadingStage('');
    }
  }, [user?.id]);

  // Fetch analysis history
  const fetchAnalysisHistory = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching analysis history:', error);
      } else {
        setAnalysisHistory(data || []);
      }
    } catch (err) {
      console.error('Error fetching analysis history:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAllUserData();
    fetchAnalysisHistory();
  }, [fetchAllUserData, fetchAnalysisHistory]);

  // Save analysis to history
  const saveAnalysisToHistory = async (report, title, userSnapshot) => {
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .insert([{
          user_id: user.id,
          report_title: title,
          analysis_report: report,
          data_snapshot: userSnapshot
        }])
        .select()
        .single();

      if (error) {
        console.error('Error saving analysis to history:', error);
      } else {
        // Add to local state
        setAnalysisHistory(prev => [data, ...prev]);
      }
    } catch (err) {
      console.error('Error saving analysis:', err);
    }
  };

  // Delete analysis from history
  const deleteAnalysis = async (analysisId) => {
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;

    try {
      const { error } = await supabase
        .from('analysis_history')
        .delete()
        .eq('id', analysisId);

      if (error) {
        console.error('Error deleting analysis:', error);
        setError('Failed to delete analysis');
      } else {
        setAnalysisHistory(prev => prev.filter(analysis => analysis.id !== analysisId));
        if (selectedHistoryReport?.id === analysisId) {
          setSelectedHistoryReport(null);
        }
      }
    } catch (err) {
      console.error('Error deleting analysis:', err);
      setError('Failed to delete analysis');
    }
  };

  // Generate AI coach report with progress indicators (OPTIMIZED)
const generateCoachReport = async () => {
  // Check if user can use AI
  if (!aiLimit.canUse) {
    setError(`You've reached your limit of ${aiLimit.limit} AI analyses this month. Upgrade to Premium for unlimited analyses!`);
    setShowPricing(true);
    return;
  }

  if (!userData.profile && userData.measurements.length === 0 && userData.workouts.length === 0) {
    setError('Not enough data available for analysis. Please add some profile information, measurements, or workouts first.');
    return;
  }

  setLoading(true);
  setError('');
  setProgress(0);
  setLoadingStage('Preparing your fitness data for analysis...');

  try {
    // Prepare user data for analysis
    setProgress(25);
    const analysisData = prepareDataForAnalysis(userData);
    
    // Truncate if too large (max ~15KB to avoid 431 error)
    const maxLength = 15000;
    const truncatedData = analysisData.length > maxLength 
      ? analysisData.substring(0, maxLength) + '\n\n[Data truncated - showing most recent activity]'
      : analysisData;
    
    const userSnapshot = {
      profile: userData.profile,
      measurementsSummary: {
        total: userData.measurements.length,
        latest: userData.measurements[userData.measurements.length - 1] || null,
        earliest: userData.measurements[0] || null
      },
      workoutsSummary: {
        total: userData.workouts.length,
        completed: userData.workouts.filter(w => w.completed).length
      },
      mealsSummary: {
        total: userData.meals.length
      },
      personalBests: userData.personalBests,
      analysisDate: new Date().toISOString()
    };
    
    setProgress(50);
    setLoadingStage('Sending data to AI coach...');
    
    // In generateCoachReport function
    const isNative = Capacitor.isNativePlatform();
    const apiUrl = isNative 
  ? 'https://iron-path.com//.netlify/functions/generate-coach-report'
  : '/.netlify/functions/generate-coach-report';

    // Call Netlify function
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        analysisData: truncatedData,  // Use truncated data
        userSnapshot: userSnapshot
      })
    });

    setProgress(75);
    setLoadingStage('AI coach is analyzing your data...');

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Server error: ${response.status}`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = `Server error: ${response.status} - ${errorText.substring(0, 100)}`;
      }
      
      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    if (!responseText) {
      throw new Error('Empty response from server');
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error('Invalid response format from server');
    }

    if (!data.report) {
      throw new Error('No report generated by the server');
    }

    setProgress(90);
    setLoadingStage('Saving your analysis...');

    const report = data.report;
    const title = data.title || `Fitness Analysis - ${new Date().toLocaleDateString()}`;
    
    // Save to history
    await saveAnalysisToHistory(report, title, userSnapshot);
    
    // Increment usage for free users
    await aiLimit.incrementUsage();
    
    setProgress(100);
    setLoadingStage('Analysis complete!');
    
    setCoachReport(report);
    setLastReportDate(new Date());
    setSelectedHistoryReport(null);

    setTimeout(() => {
      setLoadingStage('');
      setProgress(0);
    }, 1500);

  } catch (err) {
    console.error('Error generating coach report:', err);
    setError(err.message || 'Failed to generate coaching report');
    setProgress(0);
    setLoadingStage('');
  } finally {
    setLoading(false);
  }
};

  // Prepare user data for AI analysis (OPTIMIZED - Reduces data size)
const prepareDataForAnalysis = (data) => {
  let analysis = "FITNESS DATA ANALYSIS:\n\n";

  // Profile Information
  if (data.profile && Object.keys(data.profile).length > 0) {
    analysis += "PROFILE:\n";
    if (data.profile.gender) analysis += `Gender: ${data.profile.gender}\n`;
    if (data.profile.date_of_birth) {
      const age = calculateAge(data.profile.date_of_birth);
      analysis += `Age: ${age}y\n`;
    }
    if (data.profile.height) analysis += `Height: ${data.profile.height}cm\n`;
    if (data.profile.target_weight) analysis += `Target: ${data.profile.target_weight}kg\n`;
    if (data.profile.activity_level) analysis += `Activity: ${data.profile.activity_level}\n`;
    if (data.profile.fitness_goal) analysis += `Goal: ${data.profile.fitness_goal}\n`;
    if (data.profile.injuries_notes) analysis += `Notes: ${data.profile.injuries_notes}\n`;
    analysis += "\n";
  }

  // Body Measurements (summarized)
  if (data.measurements.length > 0) {
    analysis += "BODY MEASUREMENTS:\n";
    const latest = data.measurements[data.measurements.length - 1];
    const earliest = data.measurements[0];
    
    analysis += `Current (${latest.measurement_date}):\n`;
    if (latest.weight) analysis += `Weight: ${latest.weight}kg\n`;
    if (latest.body_fat_percentage) analysis += `BF: ${latest.body_fat_percentage}%\n`;
    if (latest.muscle_percentage) analysis += `Muscle: ${latest.muscle_percentage}%\n`;
    
    if (data.measurements.length > 1) {
      analysis += `Changes since ${earliest.measurement_date}:\n`;
      if (latest.weight && earliest.weight) {
        const change = (latest.weight - earliest.weight).toFixed(1);
        analysis += `Weight: ${change > 0 ? '+' : ''}${change}kg\n`;
      }
      if (latest.body_fat_percentage && earliest.body_fat_percentage) {
        const change = (latest.body_fat_percentage - earliest.body_fat_percentage).toFixed(1);
        analysis += `BF: ${change > 0 ? '+' : ''}${change}%\n`;
      }
    }
    analysis += "\n";
  }

  // Workout Analysis (summarized)
  if (data.workouts.length > 0) {
    analysis += "WORKOUTS:\n";
    const completed = data.workouts.filter(w => w.completed);
    const rate = ((completed.length / data.workouts.length) * 100).toFixed(1);
    
    analysis += `Total: ${data.workouts.length}, Completed: ${completed.length} (${rate}%)\n`;
    
    // Top exercises only (limit to 5)
    const exerciseCounts = {};
    completed.forEach(w => {
      if (w.exercise) exerciseCounts[w.exercise] = (exerciseCounts[w.exercise] || 0) + 1;
    });
    
    const topExercises = Object.entries(exerciseCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
    
    if (topExercises.length > 0) {
      analysis += "Top exercises:\n";
      topExercises.forEach(([exercise, count]) => {
        analysis += `${exercise}: ${count}x\n`;
      });
    }
    analysis += "\n";
  }

  // Personal Bests (limit to top 10)
  if (Object.keys(data.personalBests).length > 0) {
    analysis += "PERSONAL BESTS:\n";
    const pbEntries = Object.entries(data.personalBests).slice(0, 10);
    pbEntries.forEach(([exercise, best]) => {
      analysis += `${exercise}: ${best.weight}kg`;
      if (best.reps) analysis += ` x${best.reps}`;
      analysis += `\n`;
    });
    analysis += "\n";
  }

  // Nutrition (summarized)
  if (data.meals.length > 0) {
    analysis += "NUTRITION:\n";
    const days = new Set(data.meals.map(m => m.date)).size;
    const avg = (data.meals.length / days).toFixed(1);
    analysis += `${data.meals.length} meals over ${days} days (${avg}/day)\n\n`;
  }

  // Goal context
  if (data.profile.fitness_goal && data.profile.target_weight) {
    analysis += "GOALS:\n";
    analysis += `Target: ${data.profile.fitness_goal.replace('_', ' ')}\n`;
    analysis += `Weight goal: ${data.profile.target_weight}kg\n`;
    if (data.measurements.length > 0) {
      const current = data.measurements[data.measurements.length - 1].weight;
      if (current) {
        const diff = (data.profile.target_weight - current).toFixed(1);
        analysis += `${Math.abs(diff)}kg ${diff > 0 ? 'to gain' : 'to lose'}\n`;
      }
    }
    analysis += "\n";
  }
  
  analysis += "Provide personalized analysis and actionable recommendations.";

  return analysis;
};

  // Calculate age helper function
  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birth = new Date(dateOfBirth);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Enhanced format report for display with better structure
  const formatReport = (report) => {
    return report
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('**🎯') || line.startsWith('**📊') || line.startsWith('**🏆') || 
            line.startsWith('**📝') || line.startsWith('**📋') || line.startsWith('**📈')) {
          // Main section headers with emojis
          const cleanHeader = line.replace(/\*\*/g, '');
          return (
            <h3 key={index} className="text-xl font-bold text-white mt-8 mb-4 first:mt-0 pb-2 border-b border-purple-500/30">
              {cleanHeader}
            </h3>
          );
        } else if (line.startsWith('**') && line.endsWith('**')) {
          // Sub-section headers
          return (
            <h4 key={index} className="text-lg font-bold text-purple-200 mt-6 mb-3">
              {line.replace(/\*\*/g, '')}
            </h4>
          );
        } else if (line.startsWith('###')) {
          // Markdown headers
          return (
            <h5 key={index} className="text-md font-semibold text-purple-300 mt-4 mb-2">
              {line.replace(/###/g, '').trim()}
            </h5>
          );
        } else if (line.startsWith('- ')) {
          // Main bullet points
          return (
            <li key={index} className="text-purple-100 mb-2 ml-4 leading-relaxed">
              • {line.substring(2)}
            </li>
          );
        } else if (line.startsWith('  * ') || line.startsWith('  - ')) {
          // Sub-bullet points
          return (
            <li key={index} className="text-purple-200 mb-1 ml-8 text-sm leading-relaxed">
              ◦ {line.substring(4)}
            </li>
          );
        } else if (line.includes('→') || line.includes('kg') || line.includes('%')) {
          // Metrics and data lines
          return (
            <p key={index} className="text-green-300 mb-2 font-mono text-sm bg-green-900/20 rounded px-2 py-1 inline-block">
              {line}
            </p>
          );
        } else if (line.trim()) {
          // Regular paragraphs
          return (
            <p key={index} className="text-purple-100 mb-3 leading-relaxed">
              {line}
            </p>
          );
        }
        return null;
      });
  };

  return (
    <div className="space-y-8">
      {/* Show usage indicator for free users */}
      {!aiLimit.loading && aiLimit.limit !== Infinity && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-white font-semibold">AI Analyses This Month</div>
              <div className="text-purple-300 text-sm">
                {aiLimit.count} of {aiLimit.limit} used
              </div>
            </div>
            {aiLimit.count >= aiLimit.limit && (
              <button
                onClick={() => setShowPricing(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                Upgrade
              </button>
            )}
          </div>
          <div className="mt-2 w-full bg-white/10 rounded-full h-2">
            <div 
              className="bg-purple-500 h-2 rounded-full transition-all"
              style={{ width: `${(aiLimit.count / aiLimit.limit) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Enhanced Header with History Toggle */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white flex items-center mb-2">
              <img src={AICoachIcon} alt="Coach" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              AI Fitness Coach
            </h2>
            <p className="text-purple-300">
              Get comprehensive, personalized insights and recommendations based on your complete fitness journey
            </p>
            {lastReportDate && !selectedHistoryReport && (
              <p className="text-purple-400 text-sm mt-2">
                Latest analysis: {lastReportDate.toLocaleDateString()} at {lastReportDate.toLocaleTimeString()}
              </p>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="bg-blue-500/80 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm flex items-center gap-2"
            >
             {showHistory ? 'Hide' : 'Show'} History ({analysisHistory.length})
            </button>
            
            <button
              onClick={generateCoachReport}
              disabled={loading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Analyzing...
                </div>
              ) : (
                'Generate New Analysis'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      {loading && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <div className="flex items-center mb-4">
            
            <h3 className="text-xl font-bold text-white">Generating Your Analysis</h3>
          </div>
          
          <div className="space-y-4">
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between text-sm text-purple-300">
              <span>{loadingStage}</span>
              <span>{progress}%</span>
            </div>
            
            <div className="text-purple-400 text-sm">
             This typically takes 15-30 seconds. Please wait while our AI coach analyzes your complete fitness journey...
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-6">
          <div className="flex items-center">
            <span className="text-red-400 mr-3">⚠️</span>
            <div>
              <h3 className="text-red-200 font-semibold">Unable to Generate Analysis</h3>
              <p className="text-red-300 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis History */}
      {showHistory && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <img src={BlogDefaultIcon} alt="Blog" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
            Analysis History ({analysisHistory.length})
          </h3>
          
          {analysisHistory.length === 0 ? (
            <div className="text-center py-8">
              <img src={BlogDefaultIcon} alt="Blog" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              <p className="text-purple-300">No previous analyses yet.</p>
              <p className="text-purple-400 text-sm mt-2">Generate your first analysis to start building your progress history!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {analysisHistory.map((analysis) => (
                <div key={analysis.id} className="bg-white/10 border border-white/10 rounded-lg p-4 hover:bg-white/15 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">
                        {analysis.report_title || 'Fitness Analysis'}
                      </h4>
                      <p className="text-purple-300 text-sm mb-2">
                        {new Date(analysis.created_at).toLocaleDateString()} at {new Date(analysis.created_at).toLocaleTimeString()}
                      </p>
                      <p className="text-purple-400 text-xs">
                        {analysis.analysis_report.substring(0, 150)}...
                      </p>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => {
                          setSelectedHistoryReport(analysis);
                          setCoachReport(''); // Clear current report
                        }}
                        className="text-blue-300 hover:text-blue-200 text-sm px-3 py-1 rounded bg-blue-500/20"
                      >
                        View
                      </button>
                      <button
                        onClick={() => deleteAnalysis(analysis.id)}
                        className="text-red-300 hover:text-red-200 text-sm px-3 py-1 rounded bg-red-500/20"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Enhanced Statistics Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-500/20 border border-blue-500/50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">{userData.workouts.length}</div>
          <div className="text-blue-200 text-sm">Total Workouts</div>
        </div>
        <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">
            {userData.workouts.filter(w => w.completed).length}
          </div>
          <div className="text-green-200 text-sm">Completed</div>
        </div>
        <div className="bg-purple-500/20 border border-purple-500/50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">{userData.measurements.length}</div>
          <div className="text-purple-200 text-sm">Measurements</div>
        </div>
        <div className="bg-orange-500/20 border border-orange-500/50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">{analysisHistory.length}</div>
          <div className="text-orange-200 text-sm">Analyses</div>
        </div>
      </div>

      {/* AI Coach Report Display */}
      {(coachReport || selectedHistoryReport) && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <img src={BlogDefaultIcon} alt="Blog" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              {selectedHistoryReport ? 'Historical Analysis' : 'Your Personalized Coach Report'}
            </h3>
            
            {selectedHistoryReport && (
              <button
                onClick={() => {
                  setSelectedHistoryReport(null);
                  // Don't clear coachReport in case user wants to go back to current
                }}
                className="text-purple-300 hover:text-purple-200 text-sm px-3 py-1 rounded bg-purple-500/20"
              >
                ← Back to Current
              </button>
            )}
          </div>
          
          {selectedHistoryReport && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-6">
              <div className="text-blue-200 text-sm flex items-center">
                <img src={CalendarIcon} alt="Calendar" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Generated: {new Date(selectedHistoryReport.created_at).toLocaleDateString()} at {new Date(selectedHistoryReport.created_at).toLocaleTimeString()}
              </div>
              <div className="text-blue-300 font-medium mt-1">
                {selectedHistoryReport.report_title}
              </div>
            </div>
          )}
          
          <div className="prose prose-invert max-w-none">
            {formatReport(selectedHistoryReport?.analysis_report || coachReport)}
          </div>
        </div>
      )}

      {/* Getting Started */}
      {!coachReport && !selectedHistoryReport && !loading && !error && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8 text-center">
          <img src={BullseyeIcon} alt="Goal" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
          <h3 className="text-xl font-bold text-white mb-4">Ready for Your Comprehensive Fitness Analysis?</h3>
          <p className="text-purple-300 mb-6 max-w-2xl mx-auto">
            Your AI coach will analyze your complete fitness journey - workouts, nutrition, body measurements, and goals - 
            to provide personalized, actionable recommendations and track your progress over time.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto text-sm mb-8">
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-blue-400 font-medium mb-2 flex items-center"><img src={DumbbellIcon} alt="Dumbbell" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Workout Analysis</div>
              <div className="text-purple-300">Progress tracking, consistency patterns, and strength gains</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-green-400 font-medium mb-2 flex items-center"><img src={WorkoutHistoryIcon} alt="History" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Body Composition</div>
              <div className="text-purple-300">Weight trends, measurements, and body composition changes</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-purple-400 font-medium mb-2 flex items-center"><img src={DietTrackerIcon} alt="Diet" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Nutrition Insights</div>
              <div className="text-purple-300">Eating patterns, timing, and nutritional balance</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="text-orange-400 font-medium mb-2 flex items-center"><img src={BullseyeIcon} alt="Goal" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Goal Assessment</div>
              <div className="text-purple-300">Progress toward targets and personalized recommendations</div>
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 max-w-2xl mx-auto">
            <div className="text-yellow-300 text-sm">
              💡 <strong>New:</strong> All your analyses are now saved to history so you can track your progress over time 
              and see how your fitness journey evolves!
            </div>
          </div>
        </div>
      )}

      {/* Data Privacy Notice */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
        <div className="flex items-start">
          <img src={PadlockIcon} alt="Lock" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
          <div>
            <h4 className="text-yellow-200 font-medium mb-1">Privacy & Data Usage</h4>
            <p className="text-yellow-300 text-sm">
              Your fitness data is securely processed by OpenAI for analysis and saved in your personal history. 
              All data is private to your account and never shared. You can delete any analysis from your history at any time.
            </p>
          </div>
        </div>
      </div>

      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        user={user}
      />
    </div>
  );
}